import React, { useContext, useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { WebSocketStatusContext } from "Shared/context/WebSocketStatus.context"
import { useCurrentUser } from "Shared/hooks/layout/useCurrentUser"
import { useBusinessColors } from "Shared/hooks/useBusinessColors"
import { Spinner } from "Shared/components/Loaders/BasicSpinner"
import { Heading, Text } from "Shared/components/Typography"
import { useRemoteTranslation } from "Utils/useRemoteTranslation"

import {
  Body,
  CancelButton,
  ConfirmButton,
  ConfirmRow,
  Footer,
  ModalBackdrop,
  ModalContainer,
  QuietLink,
  SpinnerSlot,
} from "./index.style"

/** Délai avant d'afficher la modale : absorbe les micro-coupures sans intérêt pour l'utilisateur. */
const SHOW_AFTER_MS = 5000
/** La sortie de secours n'apparaît qu'après une coupure qui dure vraiment. */
const OFFER_RELOAD_AFTER_MS = 60_000

/**
 * Durée écoulée, formatée pour un humain.
 *
 * On affiche un temps MESURÉ, là où l'écran annonçait un « nombre de tentatives » qui n'en était
 * pas un : un compteur incrémenté chaque seconde, qui atteignait tranquillement 101 alors que le
 * client graphql-ws en tentait 5 au maximum.
 */
export const formatDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  if (totalSeconds < 60) return `${totalSeconds} s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes} min ${String(seconds).padStart(2, "0")} s`
}

/**
 * Avertit que le temps réel est coupé, tant qu'il l'est.
 *
 * **Une seule source de vérité : le socket lui-même.** L'ancienne version doublait le statut
 * graphql-ws d'un ping HTTP `/health/liveness` — un second avis, joué une seule fois, qui pouvait
 * contredire le premier et restait figé sur « le serveur semble indisponible » longtemps après le
 * retour du serveur. Depuis que le client se reconnecte sans plafond (`retryAttempts: Infinity`,
 * cf. `ApolloProviderWrapper`), le socket sait tout ce qu'il y a à savoir : s'il est coupé, on
 * attend, et son retour referme la modale de lui-même.
 *
 * L'écran reste **bloquant** : sans temps réel, les données affichées ne se rafraîchissent plus.
 * Une bannière discrète laisserait croire qu'on peut continuer à s'y fier.
 */
const WebSocketLostModal: React.FC = () => {
  const status = useContext(WebSocketStatusContext)
  const accessToken = useSelector((state: any) => state.login.jwt)
  const user = useCurrentUser()
  const { primaryColor } = useBusinessColors()

  const trad = useRemoteTranslation(user?.LANGUAGE_ID ?? 1)
  const t = trad.websocketLost || {}

  const degraded = status === "disconnected" || status === "reconnecting"

  // Temps écoulé depuis le DÉBUT de la coupure courante, remis à zéro dès que la liaison revient.
  const [elapsedMs, setElapsedMs] = useState(0)
  useEffect(() => {
    if (!degraded) {
      setElapsedMs(0)
      return undefined
    }
    const startedAt = Date.now()
    const timer = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000)
    return () => clearInterval(timer)
  }, [degraded])

  const visible = !!accessToken && degraded && elapsedMs >= SHOW_AFTER_MS

  const [confirmingReload, setConfirmingReload] = useState(false)
  useEffect(() => {
    if (!visible) setConfirmingReload(false)
  }, [visible])

  if (!visible) return null

  return (
    <>
      <ModalBackdrop />
      <ModalContainer role="alertdialog" aria-live="polite">
        <SpinnerSlot>
          <Spinner size="28" primaryColor={primaryColor} backgroundColor="transparent" />
        </SpinnerSlot>

        <Heading variant="h2">{t.title}</Heading>

        <Body>
          <Text color="secondary">{status === "reconnecting" ? t.reconnecting : t.disconnected}</Text>
          <Text variant="caption" color="muted">
            {t.unavailableFor || "Real-time updates unavailable for"} {formatDuration(elapsedMs)}
          </Text>
        </Body>

        {elapsedMs >= OFFER_RELOAD_AFTER_MS && (
          <Footer>
            {confirmingReload ? (
              <>
                <Text variant="caption" color="muted">
                  {t.reloadConfirmMessage}
                </Text>
                <ConfirmRow>
                  <ConfirmButton onClick={() => window.location.reload()}>
                    {trad.CONFIRM || "Confirm"}
                  </ConfirmButton>
                  <CancelButton onClick={() => setConfirmingReload(false)}>
                    {trad.CANCEL || "Cancel"}
                  </CancelButton>
                </ConfirmRow>
              </>
            ) : (
              <>
                <Text variant="caption" color="muted">
                  {t.reloadWarning}
                </Text>
                <QuietLink onClick={() => setConfirmingReload(true)}>{t.reloadButton}</QuietLink>
              </>
            )}
          </Footer>
        )}
      </ModalContainer>
    </>
  )
}

export default WebSocketLostModal
